// @ts-check

/**
 * CUSTOM: Mobil-swipe mellem 1. og 2. produktbillede på produktkort.
 * Markup: snippets/custom-card-swipe.liquid · CSS: assets/custom-card-swipe.css
 *
 * <custom-card-swipe> initialiserer sig selv – også for produktkort, der indlæses senere
 * (fx efter filtrering). Erstatter et inline <script>, der før kørte én gang pr. produktkort.
 */
if (!customElements.get('custom-card-swipe')) {
  /** Antal billeder man kan swipe mellem (1. og 2. billede) minus 1 */
  const MAX_SLIDE = 1;
  let resizeListenerAdded = false;

  class CustomCardSwipe extends HTMLElement {
    /**
     * Touch-enhed med skærm på højst 768px.
     * @returns {boolean}
     */
    static isTouchDevice() {
      return (
        ('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
        window.innerWidth <= 768
      );
    }

    /**
     * Ren logik (ingen DOM): hvilket slide skal vi lande på, når fingeren slippes?
     * Skift hvis man har swipet > 30 % af bredden eller hurtigt (> 0,5 px/ms), ellers snap tilbage.
     * @param {number} currentSlide Nuværende slide (0 eller 1)
     * @param {number} diffX Vandret afstand i px (positiv = swipe mod højre)
     * @param {number} width Kortets bredde i px
     * @param {number} elapsedMs Varighed af swipet i ms
     * @param {number} maxSlide Højeste slide-index
     * @returns {number} Nyt slide-index
     */
    static getSlideAfterSwipe(currentSlide, diffX, width, elapsedMs, maxSlide) {
      const velocity = Math.abs(diffX) / elapsedMs;
      if (Math.abs(diffX) > width * 0.3 || velocity > 0.5) {
        if (diffX > 0 && currentSlide > 0) return currentSlide - 1;
        if (diffX < 0 && currentSlide < maxSlide) return currentSlide + 1;
      }
      return currentSlide;
    }

    constructor() {
      super();
      this.ready = false;
    }

    connectedCallback() {
      if (this.ready || !CustomCardSwipe.isTouchDevice()) return;
      this.ready = true;

      this.closest('.grid-product__image-mask')?.classList.add('is-touch-device');
      addResizeReset();

      if (this.classList.contains('is-collection')) this.initSwipe();
    }

    initSwipe() {
      /** @type {HTMLElement | null} */
      const track = this.querySelector('.swipe-track');
      /** @type {NodeListOf<HTMLElement>} */
      const indicators = this.querySelectorAll('.swipe-indicator');
      const indicatorContainer = this.querySelector('.swipe-indicators');
      if (!track || !indicatorContainer || indicators.length === 0) return;

      let currentSlide = 0;
      let startX = 0;
      let startY = 0;
      let currentX = 0;
      let currentY = 0;
      let isDragging = false;
      let isScrolling = false;
      let startTime = 0;

      const progressBar = document.createElement('div');
      progressBar.className = 'swipe-progress-bar';
      Object.assign(progressBar.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        height: '100%',
        width: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        transition: 'none',
        pointerEvents: 'none',
        transform: 'translateX(0)',
      });
      indicatorContainer.appendChild(progressBar);

      /** @param {number} progress 0 = første billede, 1 = andet billede */
      const updateProgressBar = (progress) => {
        progressBar.style.transform = `translateX(${progress * 100}%)`;
      };

      /** @param {number} slideIndex */
      const goToSlide = (slideIndex) => {
        currentSlide = slideIndex;
        track.style.transform = `translateX(${-100 * currentSlide}%)`;
        progressBar.style.transition = 'transform 0.3s ease';
        updateProgressBar(currentSlide);
      };

      track.addEventListener('touchstart', (e) => {
        startX = currentX = e.touches[0].clientX;
        startY = currentY = e.touches[0].clientY;
        isDragging = true;
        isScrolling = false;
        startTime = Date.now();
        track.style.transition = 'none';
        progressBar.style.transition = 'none';
      });

      track.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
        currentY = e.touches[0].clientY;
        const diffX = currentX - startX;
        const diffY = currentY - startY;

        // Lodret bevægelse = brugeren scroller siden, ikke swiper
        if (!isScrolling && Math.abs(diffY) > Math.abs(diffX)) {
          isScrolling = true;
          isDragging = false;
          track.style.transition = 'transform 0.3s ease';
          goToSlide(currentSlide);
          return;
        }
        if (isScrolling) return;
        if (Math.abs(diffX) > 10) e.preventDefault();

        track.style.transform = `translateX(${-100 * currentSlide + (diffX / this.offsetWidth) * 100}%)`;
        updateProgressBar(Math.max(0, Math.min(1, currentSlide - diffX / this.offsetWidth)));
      });

      track.addEventListener('touchend', () => {
        if (!isDragging || isScrolling) return;
        isDragging = false;
        track.style.transition = 'transform 0.3s ease';
        goToSlide(
          CustomCardSwipe.getSlideAfterSwipe(
            currentSlide,
            currentX - startX,
            this.offsetWidth,
            Date.now() - startTime,
            MAX_SLIDE
          )
        );
      });

      indicators.forEach((indicator, index) => {
        indicator.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          goToSlide(index);
        });
      });

      // Et swipe må ikke tælle som klik på produktlinket
      this.addEventListener('click', (e) => {
        if (Math.abs(currentX - startX) > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }
  }

  /** Nulstil billederne, hvis skærmen gøres bredere end mobil (fx telefon vendes). */
  function addResizeReset() {
    if (resizeListenerAdded) return;
    resizeListenerAdded = true;
    /** @type {number | undefined} */
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (CustomCardSwipe.isTouchDevice()) return;
        document.querySelectorAll('.swipe-track').forEach((track) => {
          if (track instanceof HTMLElement) track.style.transform = 'translateX(0)';
        });
      }, 250);
    });
  }

  customElements.define('custom-card-swipe', CustomCardSwipe);
}
